import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import { intl } from "LocaleWrapper";
import moment, { Moment } from "moment";
import { useStoreState } from "ezStore";
import { useLazyQuery, useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormGroup, Input, Checkbox, Card, PageHeader, Button } from "@cofano/cck";

import { Box, Grid2 as Grid, IconButton, useTheme } from "@mui/material";
import RoomIcon from "@mui/icons-material/Room";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import { FormErrorMessage } from "components/form/FormErrorMessage";
import DatePicker from "components/cck/DatePicker";
import PeriodField, { getPeriodWindow } from "components/form/PeriodField";
import Helmet from "components/helmet/Helmet";
import VoyageSelect, { VoyageSelectOption } from "components/selects/VoyageSelect";
import NumberInput from "components/cck/NumberInput";

import { Language, getPluginSettingValue, hasPluginPermission, usePermission, DataFormat } from "imports/Utils";

import ShipSelect, { ShipSelectOption } from "view/ships/components/selects/ShipSelect";
import LabelSelect from "view/labels/components/selects/LabelSelect";
import { SailingReportRangeType } from "view/sailingreports/utils/SailingReportUtils";
import SailingReportTypeSelect from "view/sailingreports/components/selects/SailingReportTypeSelect";
import ShipFuelTypeSelect from "view/ships/components/selects/ShipFuelTypeSelect";

import { DateUtils, FormattedDateWindow } from "utils/DateUtils";

import SailingReportLogLocationDialog from "./components/dialogs/SailingReportLogLocationDialog";
import { sailingReportSchema, SailingReportSchemaInput, SailingReportSchemaOutput } from "./utils/sailingReport.schema";

import {
	CalculateGeofenceSailMinutesAndSailedDistanceDocument,
	GetSailingReportDocument,
	GetSailingReportQuery,
	SearchSailingReportsCompactDocument,
} from "__generated__/view/sailingreports/graphql/sailingReport.queries.generated";
import { InsertOrUpdateSailingReportDocument, InsertOrUpdateSailingReportMutation } from "__generated__/view/sailingreports/graphql/sailingReport.mutations.generated";
import { GetCompactShipDocument, GetCompactShipQuery } from "__generated__/view/ships/graphql/ship.queries.generated";
import { LabelType, SailingReportStatus, SortDirection } from "__generated__/enums";

const isPeriodRange = (rangeType: SailingReportRangeType) =>
	rangeType === SailingReportRangeType.Week || rangeType === SailingReportRangeType.Month;

const SailingReportCreateEdit = () => {

	const [ loading, setLoading ] = useState(false);
	const location = useLocation();
	const navigate = useNavigate();
	const { id } = useParams();
	const params = new URLSearchParams(location.search);

	const getWindowFromState = () => {

		if (location.state?.windowState) {
			return location.state?.windowState;
		} else if (params.get("from") && params.get("to") && params.get("shipId") && params.get("shipName")) {

			const windowObject = {
				from: Number(params.get("from")),
				to: Number(params.get("to")),
				ship: {
					id: Number(params.get("shipId")),
					name: params.get("shipName"),
				},
			};
			window.history.pushState(null, "", "/view/sailingreports/create");
			return windowObject;
		} else {
			return undefined;
		}
	};

	const voyageFromState = location.state?.voyage;
	const windowFromState = getWindowFromState();

	return (
		<div className="sailing-report-create-edit">
			<PageHeader>
				<PageHeader.Title>
					{Language("SAILING_REPORT")}
					<Helmet title={intl.formatMessage({ id: "SAILING_REPORT" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button onClick={() => navigate(id ? `/view/sailingreports/${id}/details` : "/view/sailingreports")} variant="outlined">
						{Language("CANCEL")}
					</Button>

					<Button loading={loading} form="sailingReportForm" type="submit">
						{Language("SAVE")}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			<SailingReportForm
				sailingReportId={id ? Number(id) : null}
				voyage={voyageFromState}
				windowState={windowFromState}
				onBeforeSubmit={() => setLoading(true)}
				onSuccess={(response) => navigate(`/view/sailingreports/${response.id}/details`)}
				onAfterSubmit={() => setLoading(false)} />
		</div>
	);
};

export const SailingReportForm = ({ sailingReportId, voyage, windowState, onBeforeSubmit, onSuccess, onAfterSubmit }: {
	sailingReportId?: number | null;
	voyage?: Pick<VoyageSelectOption, "id" | "voyageNumber"> & { ship?: ShipSelectOption }
	windowState?: {
		from: number,
		to: number,
		ship: ShipSelectOption;
	};
	onBeforeSubmit?: () => void;
	onSuccess?: (sailingReport: { id: number }) => void;
	onAfterSubmit?: () => void;
}) => {

	const session = useStoreState(state => state.session);

	const { isSkipper, isRTR } = usePermission();

	const [ weekWindow, setWeekWindow ] = useState(() => !sailingReportId ? {
		start: windowState ? windowState.from : moment().startOf("isoWeek").unix() * 1000,
		end: windowState ? windowState.to : moment().endOf("isoWeek").unix() * 1000,
	} : undefined);

	const pluginRangeType = getPluginSettingValue(session, "sailingReportRangeType");

	const { control, handleSubmit, reset, watch, setValue } = useForm<SailingReportSchemaInput, unknown, SailingReportSchemaOutput>({
		resolver: zodResolver(sailingReportSchema),
		defaultValues: {
			id: null,
			rangeType: windowState ? SailingReportRangeType.Week : SailingReportRangeType.Voyage,
			ship: isSkipper ? null : (voyage ? (voyage?.ship ?? null) : (windowState?.ship ?? null)),
			voyage: voyage ?? null,
			fromTime: windowState?.from ?? null,
			toTime: windowState?.to ?? null,
			remark: "",
			sailHours: null,
			geofenceSailMinutes: null,
			sailedDistanceKm: null,
			bunkeringBefore: null,
			bunkered: null,
			bunkeringAfter: null,
			fuelType: null,
			usageAmount: null,
			label: null,
			logs: [],
		},
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: "logs",
	});

	const values = watch();

	const period = values.rangeType === SailingReportRangeType.Month ? SailingReportRangeType.Month : SailingReportRangeType.Week;

	const detectRangeType = (from?: number | null, to?: number | null): SailingReportRangeType => {
		if (!from || !to) {
			return SailingReportRangeType.Voyage;
		}

		const monthWindow = getPeriodWindow(from, SailingReportRangeType.Month);
		if (moment(from).isSame(monthWindow.from, "day") && moment(to).isSame(monthWindow.to, "day")) {
			return SailingReportRangeType.Month;
		}

		return pluginRangeType === SailingReportRangeType.Week ? SailingReportRangeType.Week : SailingReportRangeType.Date;
	};

	const [ getSailingReport ] = useLazyQuery<GetSailingReportQuery>(GetSailingReportDocument, {
		onCompleted: response => {
			const detectedRangeType = detectRangeType(response.sailingReport?.fromTime, response.sailingReport?.toTime);

			reset({
				id: response.sailingReport?.id ?? null,
				rangeType: detectedRangeType,
				ship: response.sailingReport?.ship ?? null,
				voyage: response.sailingReport?.voyage ?? null,
				fromTime: response.sailingReport?.fromTime ?? null,
				toTime: response.sailingReport?.toTime ?? null,
				remark: response.sailingReport?.remark ?? "",
				sailHours: response.sailingReport?.sailMinutes ? DataFormat.rounded(response.sailingReport.sailMinutes / 60) : null,
				geofenceSailMinutes: response.sailingReport?.geofenceSailMinutes ? DataFormat.rounded(response.sailingReport.geofenceSailMinutes / 60) : null,
				sailedDistanceKm: response.sailingReport?.sailedDistanceKm ? DataFormat.rounded(response.sailingReport.sailedDistanceKm) : null,
				bunkeringBefore: response.sailingReport?.bunkeringBefore ?? null,
				bunkered: response.sailingReport?.bunkered ?? null,
				bunkeringAfter: response.sailingReport?.bunkeringAfter ?? null,
				fuelType: response.sailingReport?.fuelType ?? null,
				usageAmount: response.sailingReport?.usageAmount ?? null,
				label: response.sailingReport?.label ?? null,
				logs: response.sailingReport?.logs ? response.sailingReport.logs.map(it => ({
					id: it.id ?? null,
					allDay: it.allDay ?? false,
					startDate: it.startDate ?? null,
					endDate: it.endDate ?? null,
					remark: it.remark ?? "",
					startLocation: it.startLocation ?? "",
					startPosition: {
						latitude: it.startPosition?.latitude ?? null,
						longitude: it.startPosition?.longitude ?? null,
					},
					endLocation: it.endLocation ?? "",
					endPosition: {
						latitude: it.endPosition?.latitude ?? null,
						longitude: it.endPosition?.longitude ?? null,
					},
				})) : [],
			});

			if (isPeriodRange(detectedRangeType) && response.sailingReport?.fromTime && response.sailingReport?.toTime) {
				setWeekWindow({ start: response.sailingReport.fromTime, end: response.sailingReport.toTime });
			}
		},
	});

	const [ getPreviousWeekReport, { loading: previousReportLoading } ] = useLazyQuery(SearchSailingReportsCompactDocument, {
		onCompleted: (data) => {

			const content = data.searchSailingReports?.content ?? [];

			let bunkerLevel = 0;

			if (content.length > 0 && weekWindow) {
				const report = content[0];
				// carry over the bunker level from a previous report
				const carryOverRequiresApproval = hasPluginPermission(session, "sailingReportApprovals");
				const isApproved = report.status?.name === SailingReportStatus.Approved;
				const aligned = values.rangeType === SailingReportRangeType.Month
					? moment(report.fromTime).date() === moment(weekWindow.start).date()
					: moment(report.fromTime).isoWeekday() === moment(weekWindow.start).isoWeekday()
						&& moment(report.toTime).isoWeekday() === moment(weekWindow.end).isoWeekday();
				if (aligned && (!carryOverRequiresApproval || isApproved)) {
					bunkerLevel = report.bunkeringAfter ?? 0;
				}
			}

			setValue("bunkeringBefore", bunkerLevel);
			calculateUsage(bunkerLevel, values.bunkered, values.bunkeringAfter);
		},
	});

	const [ saveSailingReport ] = useMutation<InsertOrUpdateSailingReportMutation>(InsertOrUpdateSailingReportDocument);
	const [ calculateGeofenceSailMinutesAndSailedDistance, { loading: isCalculating } ] = useLazyQuery(CalculateGeofenceSailMinutesAndSailedDistanceDocument);
	const [ getShip ] = useLazyQuery<GetCompactShipQuery>(GetCompactShipDocument);

	const save = (data: SailingReportSchemaOutput) => {
		if (onBeforeSubmit) {
			onBeforeSubmit();
		}

		const input = {
			id: data.id ?? undefined,
			ship: {
				id: data.ship.id,
			},
			voyage: data.rangeType === SailingReportRangeType.Voyage ? {
				id: data.voyage?.id,
			} : undefined,
			fromTime: data.rangeType === SailingReportRangeType.Voyage ? undefined : isPeriodRange(data.rangeType) ? moment(weekWindow?.start).unix() * 1000 : data.fromTime,
			toTime: data.rangeType === SailingReportRangeType.Voyage ? undefined : isPeriodRange(data.rangeType) ? moment(weekWindow?.end).unix() * 1000 : data.toTime,
			remark: data.remark,
			sailMinutes: data.sailHours ? Math.round((data.sailHours * 60)) : undefined,
			bunkeringBefore: data.bunkeringBefore ? data.bunkeringBefore : undefined,
			bunkered: data.bunkered ? data.bunkered : undefined,
			bunkeringAfter: data.bunkeringAfter ? data.bunkeringAfter : undefined,
			fuelType: data.fuelType,
			usageAmount: data.usageAmount ? data.usageAmount : undefined,
			label: data.label ? {
				id: data.label.id,
			} : undefined,
			logs: data.logs.map(log => ({
				id: log.id ?? undefined,
				allDay: log.allDay,
				startDate: log.allDay && log.startDate ? moment(log.startDate).startOf("day").unix() * 1000 : log.startDate ?? undefined,
				endDate: log.allDay && log.endDate ? moment(log.endDate).endOf("day").unix() * 1000 : log.endDate ?? undefined,
				remark: log.remark,
				startLocation: log.startLocation,
				startPosition: log.startPosition.latitude && log.startPosition.longitude ? {
					latitude: log.startPosition.latitude ?? undefined,
					longitude: log.startPosition.longitude ?? undefined,
				} : undefined,
				endLocation: log.endLocation,
				endPosition: log.endPosition.latitude && log.endPosition.longitude ? {
					latitude: log.endPosition.latitude ?? undefined,
					longitude: log.endPosition.longitude ?? undefined,
				} : undefined,
			})),
		};

		saveSailingReport({
			variables: {
				input,
			},
		}).then((response) => {
			if (onSuccess && response.data?.insertOrUpdateSailingReport) {
				onSuccess(response.data.insertOrUpdateSailingReport);
			}
		}).finally(() => {
			if (onAfterSubmit) {
				onAfterSubmit();
			}
		});
	};

	const calculateUsage = (before: number | null, bunkered: number | null, after: number | null) => {
		const bunkeredBefore = before ?? 0;
		const bunkeredBunkered = bunkered ?? 0;
		const bunkeredAfter = after ?? 0;
		const usage = (bunkeredBefore + bunkeredBunkered) - bunkeredAfter;
		setValue("usageAmount", usage);
	};

	const handlePeriodChange = (newWindow: { from: Moment, to: Moment }) => {
		const start = DateUtils.getTimestamp(newWindow.from);
		const end = DateUtils.getTimestamp(newWindow.to);

		if (start && end) {
			setWeekWindow({ start, end });
		}
	};

	const applyPeriodWindow = (rangeType: SailingReportRangeType) => {
		if (!isPeriodRange(rangeType)) {
			return;
		}

		const anchor = weekWindow ? moment(weekWindow.start) : moment();

		handlePeriodChange(getPeriodWindow(anchor, rangeType === SailingReportRangeType.Month ? SailingReportRangeType.Month : SailingReportRangeType.Week));
	};

	useEffect(() => {
		if (!sailingReportId && isPeriodRange(values.rangeType) && values.ship && weekWindow) {
			const previousUnit = values.rangeType === SailingReportRangeType.Month ? "months" : "weeks";
			getPreviousWeekReport({
				variables: {
					search: {
						fromTime: moment(weekWindow.start).subtract(1, previousUnit).unix() * 1000,
						toTime: moment(weekWindow.end).subtract(1, previousUnit).unix() * 1000,
						ship: [ Number(values.ship.id) ],
					},
					pagination: {
						page: 0,
						size: 1,
						sortList: [ {
							field: "created",
							direction: SortDirection.Desc,
						} ],
					},
				},
			});
		}
	}, [ windowState, weekWindow, values.ship?.id ]);

	useEffect(() => {
		if (isRTR) {
			if (!values.ship
				|| (values.rangeType === SailingReportRangeType.Voyage && !values.voyage)
				|| (values.rangeType === SailingReportRangeType.Date && (!values.fromTime || !values.toTime))
				|| (isPeriodRange(values.rangeType) && !weekWindow)) {
				setValue("geofenceSailMinutes", null);
			} else {
				const from = values.rangeType === SailingReportRangeType.Date
					? values.fromTime
					: isPeriodRange(values.rangeType) ? moment(weekWindow?.start).unix() * 1000 : undefined;

				const to = values.rangeType === SailingReportRangeType.Date
					? values.toTime
					: isPeriodRange(values.rangeType) ? moment(weekWindow?.end).unix() * 1000 : undefined;

				calculateGeofenceSailMinutesAndSailedDistance({
					variables: {
						shipId: values.ship?.id,
						voyageId: values.rangeType === SailingReportRangeType.Voyage ? values.voyage?.id : undefined,
						from: from,
						to: to,
					},
				}).then(response => {
					setValue("geofenceSailMinutes", DataFormat.rounded((response.data?.calculateGeofenceSailMinutes ?? 0) / 60) ?? null);
					setValue("sailedDistanceKm", DataFormat.rounded(response.data?.calculateSailedDistance ?? 0) ?? null);
				});
			}
		}
	}, [ values.rangeType, values.ship, values.voyage, values.fromTime, values.toTime, weekWindow ]);

	useEffect(() => {
		if (sailingReportId) {
			getSailingReport({
				variables: {
					id: sailingReportId,
				},
			});
		}
	}, [ sailingReportId ]);

	useEffect(() => {
		if (isSkipper && session.user?.properties.shipIds?.[0]) {
			getShip({
				variables: {
					id: session.user.properties.shipIds[0],
				},
			}).then((response) => {
				setValue("ship", response.data?.ship ?? null);
			});
		}
	}, []);

	return (
		<form onSubmit={handleSubmit(save)} id="sailingReportForm">
			<Grid spacing={3} container>
				<Grid size={4}>
					<Card title={Language("DETAILS")}>
						<FormGroup label={Language("TYPE")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="rangeType"
								control={control}
								render={({ field }) => (
									<SailingReportTypeSelect
										{...field}
										onChange={(value) => {
											const newRangeType = value ?? SailingReportRangeType.Voyage;
											field.onChange(newRangeType);
											applyPeriodWindow(newRangeType);
										}} />
								)} />
						</FormGroup>

						<FormGroup
							label={Language("SHIP")}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Controller
								name="ship"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<ShipSelect
											{...field}
											disabled={isCalculating || isSkipper || !!voyage}
											onChange={(value) => {
												field.onChange(value);
												setValue("voyage", null);
												setValue("fuelType", value?.fuelType ?? null);
											}} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						{values.rangeType === SailingReportRangeType.Voyage && (
							<FormGroup
								label={Language("VOYAGE")}
								labelPlacement="left-center"
								overflow="hidden"
								required>
								<Controller
									name="voyage"
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<VoyageSelect
												{...field}
												disabled={!!voyage || isCalculating}
												searchableShipId={values.ship?.id}
												includeExpired />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)}
								/>
							</FormGroup>
						)}

						{values.rangeType === SailingReportRangeType.Date && (
							<React.Fragment>
								<FormGroup
									label={Language("FROM")}
									labelPlacement="left-center"
									overflow="hidden"
									required>
									<Controller
										name="fromTime"
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<DatePicker disabled={isCalculating} {...field} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</FormGroup>

								<FormGroup
									label={Language("TO")}
									labelPlacement="left-center"
									overflow="hidden"
									required>
									<Controller
										name="toTime"
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<DatePicker disabled={isCalculating} {...field} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</FormGroup>
							</React.Fragment>
						)}

						{isPeriodRange(values.rangeType) && (weekWindow || !sailingReportId) && (
							<FormGroup
								label={Language(period === SailingReportRangeType.Month ? "CONTRACT_MONTH" : "CONTRACT_WEEK")}
								labelPlacement="left-center"
								overflow="hidden">
								<PeriodField
									simple
									period={period}
									defaultStart={windowState ? windowState.from : weekWindow?.start}
									disabled={isCalculating}
									onChange={handlePeriodChange} />
							</FormGroup>
						)}

						<FormGroup label={Language("SAILINGREPORT_REMARK")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="remark"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<Input {...field} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup label={Language("SAILINGREPORT_SAILMINUTES")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="sailHours"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<NumberInput
											{...field}
											addonAfter={intl.formatMessage({ id: "HOURS" })}
											decimalScale={2} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						{isRTR && (
							<FormGroup
								label={Language("SAILINGREPORT_GEOFENCE_SAILMINUTES")}
								labelPlacement="left-center"
								overflow="hidden">
								<Controller
									name="geofenceSailMinutes"
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<NumberInput
												{...field}
												readOnly={true}
												disabled={true}
												addonAfter={intl.formatMessage({ id: "HOURS" })} />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)} />
							</FormGroup>
						)}

						{isRTR && (
							<FormGroup
								label={Language("SAILINGREPORT_SAILED_DISTANCE")}
								labelPlacement="left-center"
								overflow="hidden">
								<Controller
									name="sailedDistanceKm"
									control={control}
									render={({ field, formState }) => (
										<React.Fragment>
											<NumberInput
												{...field}
												readOnly
												disabled
												addonAfter={intl.formatMessage({ id: "UNIT_KM" })} />
											<FormErrorMessage name={field.name} errors={formState.errors} />
										</React.Fragment>
									)} />
							</FormGroup>
						)}

						<FormGroup label={Language("BUNKERING_BEFORE")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="bunkeringBefore"
								control={control}
								render={({ field, formState }) => {
									if (previousReportLoading) {
										return (
											<span style={{ display: "flex", flexDirection: "column", justifyContent: "center", height: 36 }}>
												{intl.formatMessage({ id: "LOADING_BUNKER_LEVEL_PREVIOUS_REPORT" })}
											</span>
										);
									} else {
										return (
											<React.Fragment>
												<NumberInput
													{ ...field }
													onChange={(value => {
														field.onChange(value);
														calculateUsage(value, values.bunkered, values.bunkeringAfter);
													})}
													addonAfter="L"
													decimalScale={0} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										);
									}
								}} />
						</FormGroup>

						<FormGroup label={Language("BUNKERED")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="bunkered"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<NumberInput
											{...field}
											onChange={(value => {
												field.onChange(value);
												calculateUsage(values.bunkeringBefore, value, values.bunkeringAfter);
											})}
											addonAfter="L"
											decimalScale={0} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup label={Language("BUNKERING_AFTER")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="bunkeringAfter"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<NumberInput
											{...field}
											onChange={(value => {
												field.onChange(value);
												calculateUsage(values.bunkeringBefore, values.bunkered, value);
											})}
											addonAfter="L"
											decimalScale={0} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup label={Language("SHIPS_FUEL_TYPE")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="fuelType"
								control={control}
								render={({ field }) => <ShipFuelTypeSelect {...field} />} />
						</FormGroup>

						<FormGroup label={Language("USAGE_AMOUNT")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="usageAmount"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<NumberInput
											{...field}
											addonAfter="L"
											decimalScale={0} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup label={Language("LABEL")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="label"
								control={control}
								render={({ field }) => <LabelSelect {...field} type={LabelType.SailingReport} />} />
						</FormGroup>
					</Card>
				</Grid>

				<Grid size={8}>
					<Card title={Language("SALINGREPORT_LOGS")} disablePadding>
						<Box p={1.5} pt={fields.length === 0 ? 1.5 : 0}>
							{fields.map((item, index) => {

								const allDay = values.logs[index].allDay;
								const hasDate = values.logs[index].startDate || values.logs[index].endDate;
								const hasLocation = values.logs[index].startLocation || values.logs[index].endLocation;
								const startLocation = values.logs[index].startLocation || intl.formatMessage({ id: "UNKNOWN" });
								const endLocation = values.logs[index].endLocation || intl.formatMessage({ id: "UNKNOWN" });

								return (
									<Card
										key={item.id}
										title={intl.formatMessage({ id: "SALINGREPORT_LOG" })}
										subtitle={(
											<React.Fragment>
												{hasDate && (
													<FormattedDateWindow
														from={values.logs[index].startDate}
														to={values.logs[index].endDate}
														format={allDay ? "ll" : undefined}
														withoutYear />
												)}

												{hasDate && hasLocation ? " / " : ""}

												{hasLocation ? `${startLocation} ${intl.formatMessage({ id: "TO" })} ${endLocation}` : ""}
											</React.Fragment>
										)}
										actions={[ {
											onClick: () => remove(index),
											icon: <DeleteIcon />,
										} ]}>
										<Grid spacing={3} container>
											<Grid size={4}>
												<FormGroup label={Language("ALL_DAY")} overflow="hidden">
													<Controller
														name={`logs.${index}.allDay`}
														control={control}
														render={({ field }) => <Checkbox {...field} checked={field.value} value={undefined} />} />
												</FormGroup>
											</Grid>

											<Grid size={4}>
												<FormGroup label={Language("SAILINGREPORT_STARTDATE")} overflow="hidden">
													<Controller
														name={`logs.${index}.startDate`}
														control={control}
														render={({ field }) => <DatePicker {...field} showTimeInput={!allDay} />} />
												</FormGroup>
											</Grid>

											<Grid size={4}>
												<FormGroup label={Language("SAILINGREPORT_ENDDATE")} overflow="hidden">
													<Controller
														name={`logs.${index}.endDate`}
														control={control}
														render={({ field }) => <DatePicker {...field} showTimeInput={!allDay} />} />
												</FormGroup>
											</Grid>

											<Grid size={4}>
												<FormGroup label={Language("SAILINGREPORT_REMARK")} overflow="hidden">
													<Controller
														name={`logs.${index}.remark`}
														control={control}
														render={({ field }) => <Input {...field} />} />
												</FormGroup>
											</Grid>

											<Grid size={4}>
												<FormGroup label={Language("SAILINGREPORT_STARTLOCATION")} overflow="hidden">
													<Controller
														name={`logs.${index}.startLocation`}
														control={control}
														render={({ field }) => (
															<LocationInput
																value={field.value}
																name={field.name}
																onChange={(value) => field.onChange(value)}
																onLocationSelect={(city, lat, lng) => {
																	field.onChange(city);
																	setValue(`logs.${index}.startPosition.latitude`, lat);
																	setValue(`logs.${index}.startPosition.longitude`, lng);
																}}
																defaultPosition={{
																	lat: values.logs[index].startPosition.latitude,
																	lng: values.logs[index].startPosition.longitude,
																}} />
														)} />

													<Controller
														name={`logs.${index}.startPosition.latitude`}
														control={control}
														render={({ field }) => <input {...field} value={field.value ?? undefined} type="hidden" />} />

													<Controller
														name={`logs.${index}.startPosition.longitude`}
														control={control}
														render={({ field }) => <input {...field} value={field.value ?? undefined} type="hidden" />} />
												</FormGroup>
											</Grid>

											<Grid size={4}>
												<FormGroup label={Language("SAILINGREPORT_ENDLOCATION")} overflow="hidden">
													<Controller
														name={`logs.${index}.endLocation`}
														control={control}
														render={({ field }) => (
															<LocationInput
																value={field.value}
																name={field.name}
																onChange={(value) => field.onChange(value)}
																onLocationSelect={(city, lat, lng) => {
																	field.onChange(city);
																	setValue(`logs.${index}.endPosition.latitude`, lat);
																	setValue(`logs.${index}.endPosition.longitude`, lng);
																}}
																defaultPosition={{
																	lat: values.logs[index].endPosition.latitude,
																	lng: values.logs[index].endPosition.longitude,
																}} />
														)} />

													<Controller
														name={`logs.${index}.endPosition.latitude`}
														control={control}
														render={({ field }) => <input {...field} value={field.value ?? undefined} type="hidden" />} />

													<Controller
														name={`logs.${index}.endPosition.longitude`}
														control={control}
														render={({ field }) => <input {...field} value={field.value ?? undefined} type="hidden" />} />
												</FormGroup>
											</Grid>
										</Grid>
									</Card>
								);
							})}

							<Box>
								<Button
									icon={<AddIcon />}
									onClick={() => {
										append({
											id: null,
											allDay: false,
											startDate: null,
											endDate: null,
											remark: "",
											startLocation: "",
											startPosition: {
												latitude: null,
												longitude: null,
											},
											endLocation: "",
											endPosition: {
												latitude: null,
												longitude: null,
											},
										});
									}}
									size="small"
									variant="outlined" />
							</Box>
						</Box>
					</Card>
				</Grid>
			</Grid>
		</form>
	);
};

const LocationInput = ({
	value,
	name,
	onChange,
	onLocationSelect,
	defaultPosition,
}: {
	value: string;
	name: string;
	onChange: (v: string) => void;
	onLocationSelect: (city: string, lat: number, lng: number) => void;
	defaultPosition: { lat: number | null, lng: number | null };
}) => {

	const [ open, setOpen ] = useState(false);

	const theme = useTheme();

	return (
		<React.Fragment>
			<Input
				value={value}
				name={name}
				onChange={event => onChange(event.target.value)}
				suffixIcon={(
					<IconButton size="small" onClick={() => setOpen(true)}>
						<RoomIcon fontSize="small" style={{ color: theme.palette.info.main }} />
					</IconButton>
				)} />

			<SailingReportLogLocationDialog
				open={open}
				onClose={() => setOpen(false)}
				onLocationSelect={onLocationSelect}
				defaultPosition={defaultPosition} />
		</React.Fragment>
	);
};

export default SailingReportCreateEdit;
